import { useInvoiceTransactionGeneralInputsBatchOptions } from '@/context/services/transaction_items/TransactionItem.service';
import { TransactionProperty } from '@/features/InvoiceReport/Edit/type';
import {
  ApGeneralInputOption,
  InvoiceTransactionDetail,
  PostApInvoiceTransactionGeneralInputsBatchOptionsBodyItem,
} from 'ap-openapi';

export const extractPulldownPropertyIds = (
  dynamicTransactionFields: TransactionProperty[]
): string[] =>
  dynamicTransactionFields
    .filter(
      ({ componentType, type }) =>
        componentType === 'pulldown' && type === 'custom'
    )
    .map(({ id }) => id);

export const buildPulldownBatchBody = (
  pulldownPropertyIds: string[],
  invoiceTransactions: InvoiceTransactionDetail[]
): PostApInvoiceTransactionGeneralInputsBatchOptionsBodyItem[] => {
  const body: PostApInvoiceTransactionGeneralInputsBatchOptionsBodyItem[] = [];
  for (const propertyId of pulldownPropertyIds) {
    const idSet = new Set<string>();
    for (const row of invoiceTransactions) {
      const raw = row.custom_transaction_items?.[propertyId];
      if (!raw) continue;
      idSet.add(raw);
    }
    const option_ids = Array.from(idSet);
    if (option_ids.length === 0) continue;
    body.push({
      ap_invoice_transaction_general_input_id: propertyId,
      option_ids,
      option_labels: [],
    });
  }
  return body;
};

export const useCustomTransactionPulldownLabels = (
  invoiceTransactions: InvoiceTransactionDetail[],
  transactionProperties: TransactionProperty[]
) => {
  const pulldownPropertyIds = extractPulldownPropertyIds(transactionProperties);
  const pulldownBatchBody = buildPulldownBatchBody(
    pulldownPropertyIds,
    invoiceTransactions
  );

  const { data, isLoading, error } =
    useInvoiceTransactionGeneralInputsBatchOptions(pulldownBatchBody);
  if (error) {
    throw error;
  }

  return {
    data: data,
    isLoading: isLoading,
  };
};

type BatchPulldownData = Array<Record<string, ApGeneralInputOption[]>>;

export const batchOptionsToNestedLabelMaps = (
  batchPulldownData: BatchPulldownData
): Map<string, Map<string, string>> => {
  const pulldownLabelsMap = new Map<string, Map<string, string>>();
  for (const pulldownData of batchPulldownData) {
    for (const [propertyId, options] of Object.entries(pulldownData)) {
      const optionIdToLabel = new Map<string, string>();
      for (const option of options) {
        if (option.id) optionIdToLabel.set(option.id, option.label ?? '');
      }
      pulldownLabelsMap.set(propertyId, optionIdToLabel);
    }
  }
  return pulldownLabelsMap;
};
